import React, { useCallback, useContext, useEffect, useState } from 'react';
import AuthContext from '../shared/AuthContext';
import { isPlatformAdmin } from '../../utils/platformRoles';
import '../admin/AdminQuestionsPage.css';

function apiError(error, fallback) {
  const data = error?.response?.data;
  if (!data) return fallback;
  if (typeof data === 'string') return data;
  return data.detail || data.message || data.description || fallback;
}

const EMPTY_FORM = {
  question: '',
  answer: '',
  extraInfo: '',
  topic: '',
  category: '',
  subCategory1: '',
  subCategory2: '',
  mediumUrl: '',
  rating: '',
  difficultyLevel: '',
  questionKind: '',
  learnNotebookId: '',
  ownerUsername: '',
  sourceDocumentId: '',
  sourcePage: '',
  sourceSection: '',
  generationQuality: '',
};

function rowToForm(row) {
  if (!row) return { ...EMPTY_FORM };
  return {
    question: row.question ?? '',
    answer: row.answer ?? '',
    extraInfo: row.extraInfo ?? '',
    topic: row.topic ?? '',
    category: row.category ?? '',
    subCategory1: row.subCategory1 ?? '',
    subCategory2: row.subCategory2 ?? '',
    mediumUrl: row.mediumUrl ?? '',
    rating: row.rating != null ? String(row.rating) : '',
    difficultyLevel: row.difficultyLevel ?? '',
    questionKind: row.questionKind ?? '',
    learnNotebookId: row.learnNotebookId != null ? String(row.learnNotebookId) : '',
    ownerUsername: row.ownerUsername ?? '',
    sourceDocumentId: row.sourceDocumentId != null ? String(row.sourceDocumentId) : '',
    sourcePage: row.sourcePage != null ? String(row.sourcePage) : '',
    sourceSection: row.sourceSection ?? '',
    generationQuality: row.generationQuality != null ? String(row.generationQuality) : '',
  };
}

const SEARCH_FIELD_OPTIONS = [
  { value: 'all', label: 'All fields' },
  { value: 'question', label: 'Question text' },
  { value: 'answer', label: 'Answer text' },
  { value: 'topic', label: 'Topic' },
  { value: 'notebook', label: 'Notebook (topic key or notebook ID)' },
  { value: 'category', label: 'Category' },
  { value: 'id', label: 'Question ID' },
];

function formToPayload(form) {
  const num = (v) => {
    if (v === '' || v == null) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  return {
    question: form.question,
    answer: form.answer,
    extraInfo: form.extraInfo,
    topic: form.topic,
    category: form.category,
    subCategory1: form.subCategory1,
    subCategory2: form.subCategory2,
    mediumUrl: form.mediumUrl,
    rating: num(form.rating),
    difficultyLevel: form.difficultyLevel,
    questionKind: form.questionKind,
    learnNotebookId: num(form.learnNotebookId),
    ownerUsername: form.ownerUsername,
    sourceDocumentId: num(form.sourceDocumentId),
    sourcePage: num(form.sourcePage),
    sourceSection: form.sourceSection,
    generationQuality: num(form.generationQuality),
    userEdited: true,
  };
}

/** Search, edit, and delete questions (creator scope or full bank for platform admins). */
export default function QuestionEditorPanel({ embedded = true }) {
  const { apiClient, user } = useContext(AuthContext);
  const profile = user || (() => {
    try {
      return JSON.parse(localStorage.getItem('userProfile') || 'null');
    } catch {
      return null;
    }
  })();
  const platformAdmin = isPlatformAdmin(profile);
  const [query, setQuery] = useState('');
  const [searchField, setSearchField] = useState('all');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [pageSize] = useState(25);
  const [loading, setLoading] = useState(true);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [meta, setMeta] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const search = useCallback(async (pageIndex = 0, qText = '', field = 'all') => {
    setLoading(true);
    setError('');
    try {
      const res = await apiClient.get('/api/admin/questions/search', {
        params: {
          q: (qText ?? '').trim(),
          field: field || 'all',
          page: pageIndex,
          size: pageSize,
        },
      });
      setRows(res.data?.items ?? []);
      setTotal(res.data?.total ?? 0);
      setPage(res.data?.page ?? pageIndex);
      setHasNext(Boolean(res.data?.hasNext));
      setInitialLoaded(true);
    } catch (err) {
      setRows([]);
      setTotal(0);
      setHasNext(false);
      setError(apiError(err, 'Search failed.'));
    } finally {
      setLoading(false);
    }
  }, [apiClient, pageSize]);

  const loadDetail = useCallback(async (id) => {
    if (!id) return;
    setError('');
    try {
      const res = await apiClient.get(`/api/admin/questions/${id}`);
      setMeta(res.data);
      setForm(rowToForm(res.data));
      setSelectedId(id);
    } catch (err) {
      setError(apiError(err, 'Could not load question.'));
    }
  }, [apiClient]);

  useEffect(() => {
    search(0, '');
  }, [search]);

  const onSubmitSearch = (e) => {
    e.preventDefault();
    search(0, query, searchField);
  };

  const onField = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const save = async () => {
    if (!selectedId) return;
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const res = await apiClient.patch(`/api/admin/questions/${selectedId}`, formToPayload(form));
      setMeta(res.data);
      setForm(rowToForm(res.data));
      setMessage('Question saved.');
      search(page, query, searchField);
    } catch (err) {
      setError(apiError(err, 'Save failed.'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!selectedId) return;
    const ok = window.confirm(`Permanently delete question #${selectedId}? This cannot be undone.`);
    if (!ok) return;
    setDeleting(true);
    setError('');
    try {
      await apiClient.delete(`/api/admin/questions/${selectedId}`);
      setMessage(`Deleted question #${selectedId}.`);
      setSelectedId(null);
      setMeta(null);
      setForm(EMPTY_FORM);
      search(page, query, searchField);
    } catch (err) {
      setError(apiError(err, 'Delete failed.'));
    } finally {
      setDeleting(false);
    }
  };

  const preview = (text, max = 72) => {
    const t = (text || '').replace(/\s+/g, ' ').trim();
    if (t.length <= max) return t || '—';
    return `${t.slice(0, max)}…`;
  };

  const TitleTag = embedded ? 'h2' : 'h1';

  return (
    <div className={`admin-q-page${embedded ? ' admin-q-page--embedded' : ''}`}>
      <header className="admin-q-head">
        <TitleTag>Question editor</TitleTag>
        <p className="admin-q-sub">
          {platformAdmin
            ? 'Platform administrator — search and edit the full question bank.'
            : `Only questions you created are listed (owner matches ${profile?.email || 'your account email'}, or from learn notebooks you own).`}
        </p>
      </header>

      <form className="admin-q-search" onSubmit={onSubmitSearch}>
        <label className="admin-q-label">
          Search in
          <select
            className="admin-q-input admin-q-select"
            value={searchField}
            onChange={(e) => setSearchField(e.target.value)}
            aria-label="Field to search"
          >
            {SEARCH_FIELD_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </label>
        <label className="admin-q-label admin-q-label--grow">
          Keywords
          <input
            className="admin-q-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={
              searchField === 'id'
                ? 'e.g. 1042'
                : searchField === 'notebook'
                  ? 'notebook topic key or numeric notebook ID'
                  : 'e.g. java or kubernetes'
            }
          />
        </label>
        <button type="submit" className="home-btn home-btn-primary" disabled={loading}>
          {loading ? 'Searching…' : 'Search'}
        </button>
      </form>

      {error && <p className="admin-q-error" role="alert">{error}</p>}
      {message && <p className="admin-q-success" role="status">{message}</p>}

      <div className="admin-q-layout">
        <section className="admin-q-list" aria-label="Search results">
          <p className="admin-q-list-meta">
            {loading && !initialLoaded
              ? 'Loading…'
              : query.trim()
                ? `${total} match${total === 1 ? '' : 'es'}`
                : `${total} total · showing page ${page + 1} (${pageSize} per page)`}
          </p>
          <ul className="admin-q-rows">
            {rows.map((row) => (
              <li key={row.id}>
                <button
                  type="button"
                  className={`admin-q-row${selectedId === row.id ? ' admin-q-row--active' : ''}`}
                  onClick={() => loadDetail(row.id)}
                >
                  <span className="admin-q-row-id">#{row.id}</span>
                  <span className="admin-q-row-title">{preview(row.question, 90)}</span>
                  <span className="admin-q-row-meta">{row.topic} · {row.subCategory2 || row.subCategory1}</span>
                </button>
              </li>
            ))}
          </ul>
          {(page > 0 || hasNext) && (
            <div className="admin-q-pager">
              <button
                type="button"
                className="home-btn home-btn-secondary"
                disabled={page <= 0 || loading}
                onClick={() => search(page - 1, query, searchField)}
              >
                Previous
              </button>
              <span>Page {page + 1}</span>
              <button
                type="button"
                className="home-btn home-btn-secondary"
                disabled={loading || !hasNext}
                onClick={() => search(page + 1, query, searchField)}
              >
                Next
              </button>
            </div>
          )}
        </section>

        <section className="admin-q-editor">
          {!selectedId ? (
            <p className="admin-q-placeholder">Select a question from the list or search by numeric ID.</p>
          ) : (
            <>
              <h3>Question #{selectedId}</h3>
              {meta?.addedOn && (
                <p className="admin-q-added">Added: {new Date(meta.addedOn).toLocaleString()}</p>
              )}

              <div className="admin-q-fields">
                <label className="admin-q-label">
                  Question
                  <textarea className="admin-q-textarea" rows={5} value={form.question} onChange={onField('question')} />
                </label>
                <label className="admin-q-label">
                  Answer
                  <textarea className="admin-q-textarea" rows={6} value={form.answer} onChange={onField('answer')} />
                </label>
                <label className="admin-q-label">
                  Extra info
                  <textarea className="admin-q-textarea" rows={5} value={form.extraInfo} onChange={onField('extraInfo')} />
                </label>

                <div className="admin-q-grid">
                  <label className="admin-q-label">
                    Topic (notebook key)
                    <input className="admin-q-input" value={form.topic} onChange={onField('topic')} />
                  </label>
                  <label className="admin-q-label">
                    Category
                    <input className="admin-q-input" value={form.category} onChange={onField('category')} />
                  </label>
                  <label className="admin-q-label">
                    Level (subCat1)
                    <input className="admin-q-input" value={form.subCategory1} onChange={onField('subCategory1')} />
                  </label>
                  <label className="admin-q-label">
                    Sub-topic (subCat2)
                    <input className="admin-q-input" value={form.subCategory2} onChange={onField('subCategory2')} />
                  </label>
                  <label className="admin-q-label">
                    Medium URL
                    <input className="admin-q-input" value={form.mediumUrl} onChange={onField('mediumUrl')} />
                  </label>
                  <label className="admin-q-label">
                    Rating
                    <input className="admin-q-input" type="number" value={form.rating} onChange={onField('rating')} />
                  </label>
                  <label className="admin-q-label">
                    Difficulty
                    <input className="admin-q-input" value={form.difficultyLevel} onChange={onField('difficultyLevel')} />
                  </label>
                  <label className="admin-q-label">
                    Question kind
                    <input className="admin-q-input" value={form.questionKind} onChange={onField('questionKind')} />
                  </label>
                  <label className="admin-q-label">
                    Learn notebook ID
                    <input className="admin-q-input" type="number" value={form.learnNotebookId} onChange={onField('learnNotebookId')} />
                  </label>
                  <label className="admin-q-label">
                    Owner username
                    <input className="admin-q-input" value={form.ownerUsername} onChange={onField('ownerUsername')} />
                  </label>
                  <label className="admin-q-label">
                    Source document ID
                    <input className="admin-q-input" type="number" value={form.sourceDocumentId} onChange={onField('sourceDocumentId')} />
                  </label>
                  <label className="admin-q-label">
                    Source page
                    <input className="admin-q-input" type="number" value={form.sourcePage} onChange={onField('sourcePage')} />
                  </label>
                  <label className="admin-q-label admin-q-label--full">
                    Source section
                    <input className="admin-q-input" value={form.sourceSection} onChange={onField('sourceSection')} />
                  </label>
                  <label className="admin-q-label">
                    Generation quality
                    <input className="admin-q-input" type="number" step="0.01" value={form.generationQuality} onChange={onField('generationQuality')} />
                  </label>
                </div>
              </div>

              <div className="admin-q-actions">
                <button type="button" className="home-btn home-btn-primary" onClick={save} disabled={saving}>
                  {saving ? 'Saving…' : 'Save changes'}
                </button>
                <button type="button" className="home-btn home-btn-secondary admin-q-delete" onClick={remove} disabled={deleting}>
                  {deleting ? 'Deleting…' : 'Delete question'}
                </button>
                <a href={`/question/${selectedId}`} className="home-btn home-btn-secondary" target="_blank" rel="noreferrer">
                  Open in app
                </a>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
